/**
 * Slice 41 — reading an ad account: what is in it, what it spent, what it
 * reached. Nothing here writes to Meta.
 *
 * ## EIGHT WAYS THIS READ GOES WRONG WHILE LOOKING RIGHT
 *
 * Every one of these produces a plausible number on a screen. They are the
 * reason this file is long, and each has a named function below and a named
 * assertion in `verify-meta-ads-read.ts`.
 *
 *   1. **Two money units in one API.** Budgets, `budget_remaining`,
 *      `amount_spent` and `spend_cap` arrive in the currency's *basic unit*
 *      ("cents for USD"). Insights `spend` arrives as a *decimal string in major
 *      units*. Read one as the other and a ₹500 daily budget is ₹50,000, or a
 *      ₹27,851.70 month is ₹278.52. {@link metaBasicUnits} and
 *      {@link metaSpendToMinor} are the only two ways in, and they are named
 *      for the unit they accept.
 *   2. **`spend_cap: "0"` means no cap.** Not a cap of zero. A screen drawing it
 *      as a limit would show every account as having exhausted its budget.
 *      {@link metaSpendCap}.
 *   3. **Reach does not add up.** Impressions, clicks and spend are additive
 *      across platforms and days. Reach is *unique people*: somebody who saw the
 *      ad on Facebook and on Instagram is one person in the account's reach and
 *      one in each platform's. Summing platform reach overstates the audience by
 *      exactly the overlap — the same arithmetic slice 39's reach ledger refuses
 *      to do across channels. So account reach comes from a separate, unbroken
 *      insights call and is never derived; {@link metaAdditiveTotals} carries no
 *      reach at all, by construction.
 *   4. **No `publisher_platforms` means everywhere.** Advantage+ placements
 *      leaves targeting without the field. Read as "no platforms", an ad set
 *      running on both Facebook and Instagram draws as running on neither.
 *      {@link metaPlacements} returns `null` for automatic, never `[]`.
 *   5. **The window is in the ad account's time zone.** Meta buckets insights
 *      by the account's reporting zone. A window computed in UTC is a day off
 *      for India after 18:30 UTC, and the figures silently cover a different
 *      30 days from the ones the tag names. {@link metaAdsWindow}.
 *   6. **A campaign with no insights row delivered nothing.** Meta omits rows
 *      rather than sending zeros — so a missing row is a real zero *when the
 *      insights call succeeded*, and "not measured" when it failed. The two
 *      draw differently, and only the transport knows which one it is.
 *   7. **An unreadable account is not an empty account.** A token without
 *      `ads_read`, or one Meta would not describe, must never produce
 *      `campaigns: []`. {@link metaAdsReadStateSchema} makes an empty list
 *      impossible outside the `ok` arm.
 *   8. **An unknown status is not "active".** Meta has a dozen
 *      `effective_status` values and adds more; anything unrecognised is
 *      `unknown` and draws as such. {@link metaDeliveryStatus}.
 *
 * ## WHY ONLY A HANDFUL OF CURRENCIES
 *
 * {@link META_CURRENCY_OFFSET} holds the currencies whose Meta offset this
 * repository can state with confidence, and a currency outside it produces
 * `null` spend — drawn as "not measured" — rather than a guessed exponent.
 * Meta's offset is not always the ISO 4217 exponent, and a wrong guess is a
 * figure off by a factor of a hundred with nothing on the screen to say so.
 * Adding a currency is adding one verified row.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';
import { metaAdsTransportSchema } from './meta-ads.js';

/* -------------------------------------------------------------------------- */
/* 1 & 2. Money                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Meta's offset per currency: how many basic units make one major unit.
 *
 * Meta publishes this per currency and it is what its budget fields are
 * denominated in. It is used for **both** directions so a figure read from a
 * budget and a figure read from insights land in the same unit: insights
 * `spend` (major) is multiplied by it; a budget (already basic) is taken as is.
 *
 * Deliberately short. See the header.
 */
export const META_CURRENCY_OFFSET: Readonly<Record<string, number>> = Object.freeze({
  INR: 100,
  USD: 100,
  EUR: 100,
  GBP: 100,
  AED: 100,
  SGD: 100,
  AUD: 100,
  CAD: 100,
  JPY: 1,
  KRW: 1,
});

/** The offset for a currency, or `null` for one this product will not guess. */
export function metaCurrencyOffset(currency: string): number | null {
  return META_CURRENCY_OFFSET[currency] ?? null;
}

/** Digits after the decimal point that a currency's major amounts may carry. */
function offsetDigits(offset: number): number {
  return Math.round(Math.log10(offset));
}

/**
 * An amount Meta already reports in **basic units** — budgets,
 * `budget_remaining`, `amount_spent`, `spend_cap`.
 *
 * Meta sends these as integer strings (`"200000"` for ₹2,000.00). Anything
 * else — a decimal, a negative, a number past `MAX_SAFE_INTEGER`, an absent
 * field — is `null`, because every one of those is either a unit mistake
 * upstream or a figure JavaScript cannot hold exactly, and "not measured" is
 * the honest drawing of both.
 */
export function metaBasicUnits(value: unknown): number | null {
  const text = typeof value === 'number' ? String(value) : value;
  if (typeof text !== 'string' || !/^\d{1,15}$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * Insights `spend` — a decimal string in **major units** — to basic units.
 *
 * Parsed as text, never through `parseFloat`: `"0.29" * 100` is
 * `28.999999999999996`, and a money figure that is out by a paisa because of
 * binary floating point is out on a screen somebody will reconcile against an
 * invoice.
 *
 * More fractional digits than the currency carries is `null`, not rounded. It
 * means our idea of the currency is wrong, and rounding would hide that.
 */
export function metaSpendToMinor(value: unknown, currency: string): number | null {
  const offset = metaCurrencyOffset(currency);
  if (offset === null) return null;

  const text = typeof value === 'number' ? String(value) : value;
  if (typeof text !== 'string') return null;

  const match = /^(\d{1,13})(?:\.(\d+))?$/.exec(text.trim());
  if (match === null) return null;

  const digits = offsetDigits(offset);
  const whole = match[1] ?? '0';
  let fraction = match[2] ?? '';
  if (fraction.length > digits) {
    /* Trailing zeros past the currency's digits are exact — `"1234.00"` for a
       zero-decimal currency is still 1234 — so they are dropped. Anything else
       past them would need rounding, and is refused instead. */
    if (!/^0+$/.test(fraction.slice(digits))) return null;
    fraction = fraction.slice(0, digits);
  }

  const minor = Number(whole) * offset + (digits === 0 ? 0 : Number(fraction.padEnd(digits, '0')));
  return Number.isSafeInteger(minor) ? minor : null;
}

/**
 * `spend_cap`, where `"0"` means **no cap**.
 *
 * Meta's own documentation: "A value of 0 means no spending-cap." Drawn as a
 * limit, it would show every uncapped account as having spent past its budget.
 */
export function metaSpendCap(value: unknown): number | null {
  const units = metaBasicUnits(value);
  return units === null || units === 0 ? null : units;
}

/** An amount of money, in basic units, in the account's currency. */
export const metaMoneySchema = z
  .object({
    minor: z.number().int().nonnegative(),
    currency: z.string().length(3),
  })
  .strict();
export type MetaMoney = z.infer<typeof metaMoneySchema>;

/* -------------------------------------------------------------------------- */
/* 8. Delivery status                                                          */
/* -------------------------------------------------------------------------- */

/**
 * What Meta's `effective_status` means for whether money is being spent.
 *
 * Coarser than Meta's list on purpose: three flavours of "paused" are one fact
 * to somebody reading a budget, and which level paused it is visible from the
 * hierarchy.
 */
export const META_DELIVERY_STATUS_VALUES = [
  'active',
  'paused',
  'in_review',
  'rejected',
  'billing',
  'issues',
  'archived',
  'deleted',
  'unknown',
] as const;
export const metaDeliveryStatusSchema = z.enum(META_DELIVERY_STATUS_VALUES);
export type MetaDeliveryStatus = z.infer<typeof metaDeliveryStatusSchema>;

/** Meta's `effective_status` → ours. Anything unrecognised is `unknown`. */
export function metaDeliveryStatus(value: unknown): MetaDeliveryStatus {
  switch (value) {
    case 'ACTIVE':
      return 'active';
    case 'PAUSED':
    case 'CAMPAIGN_PAUSED':
    case 'ADSET_PAUSED':
      return 'paused';
    case 'PENDING_REVIEW':
    case 'IN_PROCESS':
    case 'PREAPPROVED':
      return 'in_review';
    case 'DISAPPROVED':
      return 'rejected';
    case 'PENDING_BILLING_INFO':
      return 'billing';
    case 'WITH_ISSUES':
      return 'issues';
    case 'ARCHIVED':
      return 'archived';
    case 'DELETED':
      return 'deleted';
    default:
      return 'unknown';
  }
}

/* -------------------------------------------------------------------------- */
/* 4. Placements                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The placements this product names. `other` is Audience Network, Messenger
 * and anything Meta adds — real placements, not surfaces this product draws.
 *
 * `whatsapp` is slice 46's, and it means ads in WhatsApp Status — nothing else
 * on WhatsApp. An ad set reaches Status through `publisher_platforms:
 * ["whatsapp"]` with `whatsapp_positions: ["status"]`, the one WhatsApp
 * position Meta's placement reference documents. Figures are less certain:
 * Meta's own pages say marketing messages and promoted channels are bought
 * through Ads Manager too, and its insights documentation names no
 * `publisher_platform` or `platform_position` value — so a `whatsapp` row of a
 * read may hold any of them. A read therefore takes WhatsApp figures as
 * Status only by position ({@link isWhatsAppStatusPosition}, and
 * `placementFigures` in the API), and draws every other WhatsApp figure as
 * `other`. Both names — the platform's `whatsapp` and a position naming
 * Status — are assumptions no account has confirmed, chosen so that a wrong
 * one fails towards "Other placements": spend is never labelled Status that
 * the read cannot say was Status, and never dropped.
 */
export const META_PLACEMENT_VALUES = ['facebook', 'instagram', 'whatsapp', 'other'] as const;
export const metaPlacementSchema = z.enum(META_PLACEMENT_VALUES);
export type MetaPlacement = z.infer<typeof metaPlacementSchema>;

/**
 * One `publisher_platform` value → ours. `whatsapp` here is the platform; a
 * read settles which of its figures are Status by position, and an ad set's
 * targeting by its `whatsapp_positions` — see {@link metaPlacements}.
 */
export function metaPlacement(value: unknown): MetaPlacement {
  switch (value) {
    case 'facebook':
      return 'facebook';
    case 'instagram':
      return 'instagram';
    case 'whatsapp':
      return 'whatsapp';
    default:
      return 'other';
  }
}

/**
 * An ad set's `targeting.publisher_platforms`, or **`null` for automatic
 * placements** — which is what an absent field means, and which means
 * everywhere rather than nowhere.
 *
 * An explicit empty array is also `null`: Meta does not accept an empty
 * placement list, so one arriving here is not a real "nowhere" either.
 *
 * WhatsApp is Status unless the ad set names WhatsApp positions and Status is
 * not among them (slice 46): `status` is the one Meta documents, and an ad set
 * on some other WhatsApp position is `other`, not a Status ad.
 */
export function metaPlacements(targeting: unknown): MetaPlacement[] | null {
  if (typeof targeting !== 'object' || targeting === null) return null;
  const record = targeting as Record<string, unknown>;
  const platforms = record['publisher_platforms'];
  if (!Array.isArray(platforms) || platforms.length === 0) return null;
  const positions = record['whatsapp_positions'];
  const status = !Array.isArray(positions) || positions.includes('status');
  return [
    ...new Set(
      platforms.map((value): MetaPlacement => {
        const placement = metaPlacement(value);
        return placement === 'whatsapp' && !status ? 'other' : placement;
      }),
    ),
  ];
}

/**
 * Whether a WhatsApp row of a `platform_position` breakdown is Status (slice
 * 46): its position names Status. Meta documents `status` as the targeting
 * position and names no insights position at all, so this reads the name
 * rather than matching one guessed value — `status` and `whatsapp_status`
 * both count — and a position that does not name Status is not taken for it.
 */
export function isWhatsAppStatusPosition(position: unknown): boolean {
  return typeof position === 'string' && /status/i.test(position);
}

/* -------------------------------------------------------------------------- */
/* 5. The window                                                               */
/* -------------------------------------------------------------------------- */

/** How far back a read may look. Meta keeps insights far longer; screens do not need it. */
export const META_ADS_WINDOW_MAX_DAYS = 90;
export const META_ADS_WINDOW_DEFAULT_DAYS = 30;

export const metaAdsWindowSchema = z
  .object({
    /** Inclusive, `YYYY-MM-DD`, in `timezone`. */
    since: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Inclusive, `YYYY-MM-DD`, in `timezone`. Today, so its figures are partial. */
    until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The ad account's reporting zone, which is the zone Meta buckets insights in. */
    timezone: z.string(),
    days: z.number().int().min(1).max(META_ADS_WINDOW_MAX_DAYS),
  })
  .strict();
export type MetaAdsWindow = z.infer<typeof metaAdsWindowSchema>;

/** The calendar date `instant` falls on in `timezone`, as `YYYY-MM-DD`. */
function dateIn(instant: Date, timezone: string): string {
  /* `en-CA` formats as YYYY-MM-DD. The zone is the account's, never the
     server's — the server's zone is the one thing this must not depend on. */
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/**
 * The last `days` calendar days **including today**, in the account's zone.
 *
 * Calendar arithmetic is done on a UTC midnight built from the local date's
 * digits, so a daylight-saving change inside the window cannot move a
 * boundary: the result is "the date N-1 days before today's date", never
 * "the instant 24×(N-1) hours ago".
 *
 * Returns `null` for a zone `Intl` does not know, rather than falling back to
 * UTC — a window quietly moved to another zone is the defect this exists to
 * prevent.
 */
export function metaAdsWindow(now: Date, days: number, timezone: string): MetaAdsWindow | null {
  if (!Number.isInteger(days) || days < 1 || days > META_ADS_WINDOW_MAX_DAYS) return null;

  let until: string;
  try {
    until = dateIn(now, timezone);
  } catch {
    return null;
  }

  const [y, m, d] = until.split('-').map(Number) as [number, number, number];
  const sinceDate = new Date(Date.UTC(y, m - 1, d - (days - 1)));
  const since = sinceDate.toISOString().slice(0, 10);

  return { since, until, timezone, days };
}

/* -------------------------------------------------------------------------- */
/* 3 & 6. Figures                                                              */
/* -------------------------------------------------------------------------- */

/**
 * What insights reported for one object over one window.
 *
 * Every field is nullable independently, because Meta can answer for some and
 * not others, and a figure it did not report is not a zero. `reach` is here —
 * it is true of *this* object over *this* window — and is the one field no
 * function in this file ever adds to another.
 */
export const metaAdsFiguresSchema = z
  .object({
    impressions: z.number().int().nonnegative().nullable(),
    clicks: z.number().int().nonnegative().nullable(),
    /** Basic units, in the account currency. */
    spend: z.number().int().nonnegative().nullable(),
    /** Unique people. NOT additive — see the header, trap 3. */
    reach: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type MetaAdsFigures = z.infer<typeof metaAdsFiguresSchema>;

/**
 * The figures of an object that delivered nothing in the window.
 *
 * Only ever used when the insights call **succeeded** and simply had no row for
 * the object — Meta's convention for "no activity". A failed call produces
 * {@link META_ADS_FIGURES_UNMEASURED} instead. See the header, trap 6.
 */
export const META_ADS_FIGURES_NONE: MetaAdsFigures = Object.freeze({
  impressions: 0,
  clicks: 0,
  spend: 0,
  reach: 0,
});

/** The figures of an object nobody could measure. Every field null. */
export const META_ADS_FIGURES_UNMEASURED: MetaAdsFigures = Object.freeze({
  impressions: null,
  clicks: null,
  spend: null,
  reach: null,
});

/** One insights row's figures, decoded in the account currency. */
export function metaAdsFigures(row: unknown, currency: string): MetaAdsFigures {
  if (typeof row !== 'object' || row === null) return META_ADS_FIGURES_UNMEASURED;
  const source = row as Record<string, unknown>;
  return {
    impressions: metaBasicUnits(source['impressions']),
    clicks: metaBasicUnits(source['clicks']),
    spend: metaSpendToMinor(source['spend'], currency),
    reach: metaBasicUnits(source['reach']),
  };
}

/** The additive half of a set of figures. There is no `reach` on it to add. */
export interface MetaAdditiveTotals {
  readonly impressions: number | null;
  readonly clicks: number | null;
  readonly spend: number | null;
}

/**
 * Sums the three figures that ARE additive, and nothing else.
 *
 * The return type has no `reach` field, which is the rule made structural
 * rather than remembered: there is no way to get a summed reach out of this
 * function, so there is no way to draw one by accident.
 *
 * A null anywhere makes that total null — one unmeasured platform means the
 * total is unmeasured, not smaller.
 */
export function metaAdditiveTotals(figures: readonly MetaAdsFigures[]): MetaAdditiveTotals {
  const sum = (pick: (one: MetaAdsFigures) => number | null): number | null => {
    let total = 0;
    for (const one of figures) {
      const value = pick(one);
      if (value === null) return null;
      total += value;
    }
    return total;
  };
  return {
    impressions: sum((one) => one.impressions),
    clicks: sum((one) => one.clicks),
    spend: sum((one) => one.spend),
  };
}

/* -------------------------------------------------------------------------- */
/* Inventory                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A budget, at whichever level holds it.
 *
 * Campaign budget optimisation puts the budget on the campaign and none on its
 * ad sets; the other way round puts it on the ad sets and none on the
 * campaign. Neither is a zero budget, so an object without one has `null`.
 */
export const metaBudgetSchema = z
  .object({
    kind: z.enum(['daily', 'lifetime']),
    amount: z.number().int().nonnegative(),
    /** Basic units left, when Meta reports it. */
    remaining: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type MetaBudget = z.infer<typeof metaBudgetSchema>;

/** A budget off a campaign or ad set object, or `null` when it holds none. */
export function metaBudget(source: Record<string, unknown>): MetaBudget | null {
  const daily = metaBasicUnits(source['daily_budget']);
  const lifetime = metaBasicUnits(source['lifetime_budget']);
  const remaining = metaBasicUnits(source['budget_remaining']);
  /* A budget of "0" is not a budget: Meta reports it on objects whose budget
     lives at the other level. */
  if (daily !== null && daily > 0) return { kind: 'daily', amount: daily, remaining };
  if (lifetime !== null && lifetime > 0) return { kind: 'lifetime', amount: lifetime, remaining };
  return null;
}

export const metaAdCampaignSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    /** Meta's objective, verbatim — e.g. `OUTCOME_LEADS`. A label, not a behaviour. */
    objective: z.string().nullable(),
    status: metaDeliveryStatusSchema,
    budget: metaBudgetSchema.nullable(),
    /** Over the read's window. See {@link META_ADS_FIGURES_NONE}. */
    figures: metaAdsFiguresSchema,
  })
  .strict();
export type MetaAdCampaign = z.infer<typeof metaAdCampaignSchema>;

export const metaAdSetSchema = z
  .object({
    id: z.string(),
    campaignId: z.string(),
    name: z.string(),
    status: metaDeliveryStatusSchema,
    budget: metaBudgetSchema.nullable(),
    /** `null` is automatic placements — everywhere, not nowhere. */
    placements: z.array(metaPlacementSchema).nullable(),
    figures: metaAdsFiguresSchema,
  })
  .strict();
export type MetaAdSet = z.infer<typeof metaAdSetSchema>;

export const metaAdSchema = z
  .object({
    id: z.string(),
    adSetId: z.string(),
    campaignId: z.string(),
    name: z.string(),
    status: metaDeliveryStatusSchema,
  })
  .strict();
export type MetaAd = z.infer<typeof metaAdSchema>;

/* -------------------------------------------------------------------------- */
/* The account read                                                            */
/* -------------------------------------------------------------------------- */

export const metaAdsPlatformFiguresSchema = z
  .object({ placement: metaPlacementSchema, figures: metaAdsFiguresSchema })
  .strict();
export type MetaAdsPlatformFigures = z.infer<typeof metaAdsPlatformFiguresSchema>;

/**
 * What one read of an ad account learned.
 *
 * `figures` is the account over the window **from its own unbroken insights
 * call** — its `reach` is the true count of people, overlap removed. It is not
 * derived from `byPlacement`, and must not be: see the header, trap 3.
 *
 * `truncated` is true when paging stopped at the cap before Meta ran out of
 * campaigns. A screen that drew a partial list as the whole account would be
 * the no-silent-caps rule broken quietly.
 */
export const metaAdsAccountReadSchema = z
  .object({
    transport: metaAdsTransportSchema,
    readAt: isoDateTimeSchema,
    currency: z.string().length(3),
    window: metaAdsWindowSchema,
    figures: metaAdsFiguresSchema,
    byPlacement: z.array(metaAdsPlatformFiguresSchema),
    /** Lifetime-to-date against the cap, basic units. */
    amountSpent: z.number().int().nonnegative().nullable(),
    /** `null` is no cap — Meta's `"0"`. See trap 2. */
    spendCap: z.number().int().positive().nullable(),
    campaigns: z.array(metaAdCampaignSchema),
    truncated: z.boolean(),
  })
  .strict();
export type MetaAdsAccountRead = z.infer<typeof metaAdsAccountReadSchema>;

/** One campaign opened up: its ad sets and ads over the same window. */
export const metaAdsCampaignReadSchema = z
  .object({
    transport: metaAdsTransportSchema,
    readAt: isoDateTimeSchema,
    currency: z.string().length(3),
    window: metaAdsWindowSchema,
    campaignId: z.string(),
    adSets: z.array(metaAdSetSchema),
    ads: z.array(metaAdSchema),
    truncated: z.boolean(),
  })
  .strict();
export type MetaAdsCampaignRead = z.infer<typeof metaAdsCampaignReadSchema>;

/* -------------------------------------------------------------------------- */
/* 7. The states a read can end in                                             */
/* -------------------------------------------------------------------------- */

/**
 * Why a read could not produce figures, in terms of what somebody does next.
 *
 * `not_checked` and `not_permitted` are the slice 40 grant's two refusals and
 * they stay apart here for the same reason they are apart there: one asks for
 * a Test, the other for App Review.
 *
 * `rate_limited` and `read_limit` are two different sentences about two
 * different limits, and merging them would make the product say Meta throttled
 * an account when it did not (slice 52, gap 5). `rate_limited` is **Meta**
 * throttling the account — wait, and it clears on Meta's clock. `read_limit`
 * is **Wend's own** limit on how often a workspace may read its ad account,
 * which no Graph request was made against: Wend refused before asking. This
 * list is longer than `META_ADS_FAILURE_CODE_VALUES` by exactly that code,
 * deliberately — a connection check cannot fail this way, because nothing
 * about it is a read.
 */
export const metaAdsReadFailureSchema = z
  .object({
    code: z.enum([
      'invalid_token',
      'token_expired',
      'missing_ads_permission',
      'access_denied',
      'unknown_object',
      'ad_account_disabled',
      'rate_limited',
      'read_limit',
      'network',
      'unknown',
    ]),
    message: z.string(),
  })
  .strict();
export type MetaAdsReadFailure = z.infer<typeof metaAdsReadFailureSchema>;

/**
 * What a read refused by Wend's own limit says — on the card's "Not read" chip
 * with its Try again, on Integrations, on a draft's page and on Analytics
 * alike, and on the ad sheets' reason line in a workbook.
 *
 * No number of seconds on a screen: `Retry-After` carries the number for an
 * HTTP caller, and a sentence that quotes one goes stale the instant it is
 * drawn. It never says Meta — Meta was not asked.
 */
export const META_READ_LIMIT_REASON = 'Wend limits how often it reads your ad account. Try again in a minute.';

/**
 * What `GET /meta-ads/account` answers with.
 *
 * **An empty campaign list can only appear inside `ok`.** Every other arm
 * carries no list at all, so a workspace whose token cannot read has no
 * `campaigns: []` for a screen to draw as "this account is empty". That is the
 * whole point of the union: zero and unknown are different arms, not the same
 * array.
 */
export const metaAdsReadStateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('ok'), read: metaAdsAccountReadSchema }).strict(),
  z.object({ state: z.literal('not_connected') }).strict(),
  /** Connected, and Meta has not said what the token may do. Test it. */
  z.object({ state: z.literal('not_checked') }).strict(),
  /** Meta said the token may not read ads. App Review, or regrant the system user. */
  z.object({ state: z.literal('not_permitted') }).strict(),
  /** The read reached Meta and Meta refused it. */
  z.object({ state: z.literal('failed'), failure: metaAdsReadFailureSchema }).strict(),
]);
export type MetaAdsReadState = z.infer<typeof metaAdsReadStateSchema>;

/** The same states for one campaign opened up. */
export const metaAdsCampaignReadStateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('ok'), read: metaAdsCampaignReadSchema }).strict(),
  z.object({ state: z.literal('not_connected') }).strict(),
  z.object({ state: z.literal('not_checked') }).strict(),
  z.object({ state: z.literal('not_permitted') }).strict(),
  z.object({ state: z.literal('failed'), failure: metaAdsReadFailureSchema }).strict(),
]);
export type MetaAdsCampaignReadState = z.infer<typeof metaAdsCampaignReadStateSchema>;

/** `GET /meta-ads/account?days=` — the only query parameter. */
export const metaAdsReadQuerySchema = z
  .object({
    days: z.coerce
      .number()
      .int()
      .min(1)
      .max(META_ADS_WINDOW_MAX_DAYS)
      .default(META_ADS_WINDOW_DEFAULT_DAYS),
  })
  .strict();
export type MetaAdsReadQuery = z.infer<typeof metaAdsReadQuerySchema>;
